import { useState, useCallback, useEffect } from "react";
import i18n from '@/i18n';
import SingleCustomCollectionService from "@/services/SingleCustomCollectionService";
import CustomCollectionsService from "@/services/CustomCollectionsServiceNew";

/**
 * useSingleCustomCollection Hook - Comprehensive hook for managing individual custom collection items
 * Handles CRUD operations, filtering, sorting, and pagination for items within a specific custom collection
 */
export default function useSingleCustomCollection(collectionId, initialFilterFieldName = null, initialFilterFieldValue = null) {
  // Data State
  const defLimit = 50;
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [collectionName, setCollectionName] = useState(null);
  const [collectionDetails, setCollectionDetails] = useState(null);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Error/Message State
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Filter/Sort State
  const [filters, setFilters] = useState({})
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [limit, setLimit] = useState(defLimit);

  /**
   * Reset all state to initial values
   */
  const resetState = useCallback(() => {
    setItems([]);
    setCurrentItem(null);
    setCollectionName(null);
    setCollectionDetails(null);
    setLoading(true);
    setLoadingMore(false);
    setLoadingItem(false);
    setLoadingCollection(false);
    setCreating(false);
    setUpdating(false);
    setDeleting(false);
    setError(null);
    setMessage(null);
    setTotalCount(0);
    setHasMore(false);
    setFilters({});
    setSortColumn('created');
    setSortDirection('desc');
    setLimit(defLimit);
  }, []);

  /**
   * Load collection details by ID
   */
  const loadCollectionDetails = useCallback(async () => {
    // console.log("TCL -> useSingleCustomCollection -> collectionId:", collectionId)
    if (!collectionId) {
      setError(i18n.t('singleCustomCollection.list.messages.collectionIdRequired'));
      return;
    }

    setLoadingCollection(true);
    setError(null);

    const result = await CustomCollectionsService.getCustomCollectionById(collectionId);

    if (result.error) {
      setError(result.error);
      setCollectionDetails(null);
      setCollectionName(null);
    } else {
      // ensure sort defaults are applied before triggering item load
      if (result.data?.sortField) setSortColumn(result.data.sortField);
      if (result.data?.sortDirection) setSortDirection(result.data.sortDirection);
      setCollectionDetails(result.data);
      setCollectionName(result.data?.name || null);
    }

    setLoadingCollection(false);
  }, [collectionId]);

  /**
   * Load items from the custom collection with current filters and pagination
   */
  const loadItems = useCallback(async () => {
    const subCollectionFilters = { ...filters };

    if (initialFilterFieldName && initialFilterFieldValue !== undefined && initialFilterFieldValue !== null) {
      subCollectionFilters[initialFilterFieldName] = initialFilterFieldValue;
    }

    if (!collectionName) {
      setError(i18n.t('singleCustomCollection.list.messages.collectionNameUnavailable'));
      setLoading(false);
      return;
    }

    setLoadingMore(prevLoadingMore => {
      if (!prevLoadingMore) setLoading(true);
      return prevLoadingMore;
    });
    setError(null);

    const result = await SingleCustomCollectionService.getCustomCollectionItems(collectionName, {
      limit,
      sortColumn,
      sortDirection,
      filters: subCollectionFilters
    });

    if (result.error) {
      setError(result.error);
      setItems([]);
      setTotalCount(0);
      setHasMore(false);
    } else {
      setItems(result.data);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
    }
    
    setLoading(false);
    setLoadingMore(false);
  }, [collectionName, limit, sortColumn, sortDirection, filters]);

  /**
   * Load more items (pagination)
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      setLimit(prevLimit => prevLimit + 50);
      setLoadingMore(true);
    }
  }, [hasMore, loadingMore]);

  /**
   * Load a single item by ID
   */
  const loadItemById = useCallback(async (itemId) => {
    if (!collectionName || !itemId) {
      setError(i18n.t('singleCustomCollection.list.messages.collectionAndItemIdRequired'));
      return;
    }

    setLoadingItem(true);
    setError(null);

    const result = await SingleCustomCollectionService.getCustomCollectionItemById(collectionName, itemId);

    if (result.error) {
      setError(result.error);
      setCurrentItem(null);
    } else {
      setCurrentItem(result.data);
    }

    setLoadingItem(false);
  }, [collectionName]);

  /**
   * Create a new item in the custom collection
   */
  const createItem = useCallback(async (itemData) => {
    if (!collectionName) {
      const translatedError = i18n.t('singleCustomCollection.list.messages.collectionNameUnavailable');
      setError(translatedError);
      return { success: false, error: translatedError };
    }

    setCreating(true);
    setError(null);
    setMessage(null);

    const dataWithCollection = {
      ...itemData,
      collection: collectionName
    };

    const result = await SingleCustomCollectionService.createCustomCollectionItem(dataWithCollection);

    if (result.success) {
      setMessage(i18n.t('singleCustomCollection.list.messages.createdSuccess'));
      // Refresh the items list
      await loadItems();
    } else {
      setError(result.error);
    }

    setCreating(false);
    return result;
  }, [collectionName, loadItems]);

  /**
   * Update an existing item in the custom collection
   */
  const updateItem = useCallback(async (itemId, itemData) => {
    if (!collectionName || !itemId) {
      const translatedError = i18n.t('singleCustomCollection.list.messages.collectionAndItemIdRequired');
      setError(translatedError);
      return { success: false, error: translatedError };
    }

    setUpdating(true);
    setError(null);
    setMessage(null);

    const dataWithCollection = {
      ...itemData,
      collection: collectionName
    };

    const result = await SingleCustomCollectionService.updateCustomCollectionItem(itemId, dataWithCollection);

    if (result.success) {
      setMessage(i18n.t('singleCustomCollection.list.messages.updatedSuccess'));
      // Update current item if it's the one being edited
      if (currentItem && currentItem._id === itemId) {
        setCurrentItem(result.data);
      }
      // Refresh the items list
      await loadItems();
    } else {
      setError(result.error);
    }

    setUpdating(false);
    return result;
  }, [collectionName, currentItem, loadItems]);

  /**
   * Delete an item from the custom collection
   */
  const deleteItem = useCallback(async (itemId) => {
    if (!collectionName || !itemId) {
      const translatedError = i18n.t('singleCustomCollection.list.messages.collectionAndItemIdRequired');
      setError(translatedError);
      return { success: false, error: translatedError };
    }

    setDeleting(true);
    setError(null);
    setMessage(null);

    const result = await SingleCustomCollectionService.deleteCustomCollectionItem(collectionName, itemId);

    if (result.success) {
      setMessage(i18n.t('singleCustomCollection.list.messages.deletedSuccess'));
      // Clear current item if it's the one being deleted
      if (currentItem && currentItem._id === itemId) {
        setCurrentItem(null);
      }
      // Refresh the items list
      await loadItems();
    } else {
      setError(result.error);
    }

    setDeleting(false);
    return result;
  }, [collectionName, currentItem, loadItems]);

  /**
   * Update sorting
   */
  const setSorting = useCallback((column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  /**
   * Reset pagination to initial state
   */
  const resetPagination = useCallback(() => {
    setLimit(defLimit);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear message state
   */
  const clearMessage = useCallback(() => {
    setMessage(null);
  }, []);

  /**
   * Clear current item
   */
  const clearCurrentItem = useCallback(() => {
    setCurrentItem(null);
  }, []);

  /**
   * Initialize collection - loads collection details and items
   */
  const initializeCollection = useCallback(async () => {
    await loadCollectionDetails();
  }, [loadCollectionDetails]);

  /**
   * Reset state when collectionId changes
   */
  useEffect(() => {
    resetState();
  }, [collectionId, resetState]);

  return {
    // Data
    items,
    currentItem,
    collectionName,
    collectionDetails,
    totalCount,
    hasMore,
    
    // UI States
    loading,
    loadingMore,
    loadingItem,
    loadingCollection,
    creating,
    updating,
    deleting,
    error,
    message,
    
    // Filter/Sort State
    filters,
    sortColumn,
    sortDirection,
    limit,
    
    // CRUD Actions
    createItem,
    updateItem,
    deleteItem,
    loadItemById,
    
    // Collection Actions
    loadCollectionDetails,
    initializeCollection,
    
    // List Actions
    loadItems,
    handleLoadMore,
    
    // Filter/Sort Actions
    setSorting,
    updateFilters,
    clearFilters,
    resetPagination,
    
    // State management
    setFilters,
    clearError,
    clearMessage,
    clearCurrentItem,
    setCurrentItem,
    resetState
  };
}
